import type { Country } from './common';

/**
 * Implements:
 * http://www.skatteetaten.no/en/Person/National-Registry/Birth-and-name-selection/Children-born-in-Norway/National-ID-number/
 */
export class NO implements Country {
	private static readonly VALIDATION_REGEX = /^[0-9]{11}$/i;

	/** `DDMMYYXXXXX` */
	public readonly minLength = 11;

	public validate(input: string): boolean {
		if (!NO.VALIDATION_REGEX.test(input)) return false;

		// No normalization
		const normalized = input;

		// Validate check digits
		if (!this.validateNorwegianBirthNumber(normalized)) return false;

		return true;
	}

	public normalize(input: string): string {
		return input;
	}

	public getDateOfBirth(input: string): Date | null {
		if (!this.validate(input)) return null;

		const dateOfBirth = this.normalize(input).slice(0, 9);
		const number = +dateOfBirth.slice(6, 9);
		let century = '18';

		if (number < 500) {
			century = '19';
		} else {
			century = '20';
		}

		const year = +`${century}${dateOfBirth.slice(4, 6)}`;
		const month = +dateOfBirth.slice(2, 4);
		let day = +dateOfBirth.slice(0, 2);

		// If it's a temporary nin, then correct the birthday
		if (day > 40) {
			day -= 40;
		}

		return new Date(year, month - 1, day, 0, 0, 0, 0);
	}

	/**
	 * Validates a norwegian Birth Number by verifying its check digits.
	 */
	private validateNorwegianBirthNumber(birthNumber: string): boolean {
		// https://en.wikipedia.org/wiki/National_identification_number#Norway (doesn't contain detailed info)
		// https://github.com/miles-no/no-validation-scala/blob/master/src/main/scala/no/sand/eirik/novalidation/ValidBirthNumber.scala (implementation from here)

		// Each check digit is calculated by multiplying every digit to the left of it with
		// a specific factor per index, summing the result and then doing 11 - sum % 11.
		// If the calculated digits match those in the input number, the number is valid.

		const digit1 = this.validateNorwegianBirthNumber_Checksum(birthNumber, [3, 7, 6, 1, 8, 9, 4, 5, 2]);
		const digit2 = this.validateNorwegianBirthNumber_Checksum(birthNumber, [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]);

		return parseInt(birthNumber[9]) === digit1 && parseInt(birthNumber[10]) === digit2;
	}

	private validateNorwegianBirthNumber_Checksum(birthNumber: string, factors: number[]): number {
		let sum = 0;
		for (let n = 0; n < factors.length; n++) sum += factors[n] * parseInt(birthNumber.charAt(n));

		const result = 11 - (sum % 11);

		return result === 11 ? 0 : result;
	}
}
